import { useEffect, useRef, useState } from "react";
import { getLiveSocket } from "../../lib/socket.js";
import { useT } from "../../i18n/index.js";
import type { Socket } from "socket.io-client";

interface WaitingRoomProps {
  sessionId: string;
  playerId: string;
  playerToken: string;
  nickname: string;
  onGameStarted: () => void;
}

function configureSocketAuth(socket: Socket, auth: Record<string, string>): void {
  if (socket.connected && JSON.stringify(socket.auth ?? {}) !== JSON.stringify(auth)) {
    socket.disconnect();
  }
  socket.auth = auth;
}

export function WaitingRoom({
  sessionId,
  playerId,
  playerToken,
  nickname,
  onGameStarted,
}: WaitingRoomProps) {
  const t = useT();
  const socketRef = useRef<Socket | null>(null);
  const [kicked, setKicked] = useState(false);
  const [playerCount, setPlayerCount] = useState(1);

  useEffect(() => {
    const socket = getLiveSocket();
    socketRef.current = socket;
    configureSocketAuth(socket, { type: "player", playerToken, sessionId });

    function onConnect() {
      socket.emit("player:joinRoom", {});
    }
    function onPlayersChanged(data: { players: Array<{ id: string }> }) {
      setPlayerCount(data.players.length);
    }
    function onKicked(data: { requestId?: string } & Record<string, unknown>) {
      void data;
      setKicked(true);
    }
    function onStateChanged(data: { phase: string }) {
      if (data.phase === "question_intro" || data.phase === "answering") {
        onGameStarted();
      }
    }
    function onSnapshot(data: { player?: { id: string }; host?: { players: Array<{ id: string }> } }) {
      if (data.host?.players) setPlayerCount(data.host.players.length);
    }

    socket.on("connect", onConnect);
    socket.on("lobby:playersChanged", onPlayersChanged);
    socket.on("player:kicked", onKicked);
    socket.on("session:stateChanged", onStateChanged);
    socket.on("session:snapshot", onSnapshot);

    if (socket.connected) onConnect();
    else socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("lobby:playersChanged", onPlayersChanged);
      socket.off("player:kicked", onKicked);
      socket.off("session:stateChanged", onStateChanged);
      socket.off("session:snapshot", onSnapshot);
    };
  }, [sessionId, playerId, playerToken, onGameStarted]);

  if (kicked) {
    return (
      <div className="player-wait-page">
        <div className="player-kicked-card">
          <p className="player-kicked-heading">{t("wait.removedTitle")}</p>
          <p className="player-kicked-body">{t("wait.removedBody")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="player-wait-page" aria-live="polite">
      <div className="player-wait-inner">
        <p className="player-wait-eyebrow">{t("wait.joinedAs")}</p>
        <h1 className="player-wait-name">{nickname}</h1>
        <div className="player-wait-card">
          <div className="player-wait-dot" aria-hidden="true" />
          <p className="player-wait-text">{t("wait.waitingForHost")}</p>
          <p className="player-wait-count" role="status">
            {t("wait.playerCount", { count: playerCount, suffix: playerCount === 1 ? "" : "s" })}
          </p>
        </div>
      </div>
    </div>
  );
}
